import classNames from "classnames";
import parse, { attributesToProps } from "html-react-parser";
import React from "react";
import ContentNavigation from "./ContentNavigation";
import AnchorTag from "./dom-parse-components/AnchorTag";
import ImageTag from "./dom-parse-components/ImageTag";
import TableTag from "./dom-parse-components/TableTag";
import TBodyTag from "./dom-parse-components/TBodyTag";
import TdTag from "./dom-parse-components/TdTag";
import TFootTag from "./dom-parse-components/TFootTag";
import THeadTag from "./dom-parse-components/THeadTag";
import ThTag from "./dom-parse-components/ThTag";
import TrTag from "./dom-parse-components/TrTag";

const DOMTagComponentMap = {
  a: AnchorTag,
  img: ImageTag,
  table: TableTag,
  tr: TrTag,
  td: TdTag,
  thead: THeadTag,
  tbody: TBodyTag,
  tfoot: TFootTag,
  th: ThTag,
};

let keyCounter = 0;

const generateUniqueKey = () => {
  return `dom-node-${keyCounter++}`;
};

const parseAndTransformNode = (node) => {
  if (node.nodeType === 3) {
    return parse(node.data || "", {
      replace: (domNode: any) => {
        if (domNode?.name) return parseAndTransformNode(domNode);
        return domNode;
      },
    });
  }

  if (node.nodeType === 1) {
    const { name, attribs, childNodes } = node;

    const isSelfClosingTag = !childNodes?.length;
    const props = attributesToProps(attribs);

    const taggedProps = Object.entries({ class: "", ...(props || {}) }).reduce(
      (prev, [key, value]) => {
        if (key === "class")
          return {
            ...prev,
            className: classNames("auto-generated", value),
          };
        return {
          ...prev,
          [key]: value,
        };
      },
      {},
    );

    let children = null;

    if (!isSelfClosingTag) {
      children = Array.from(childNodes).map((childNode) =>
        parseAndTransformNode(childNode),
      );
    }

    const Component = DOMTagComponentMap[name];
    const uniqueKey = generateUniqueKey();
    if (Component) {
      return (
        <Component key={uniqueKey} {...taggedProps}>
          {children}
        </Component>
      );
    } else {
      const WrapperComponent = name.toLowerCase();
      return React.createElement(
        WrapperComponent,
        { key: uniqueKey, ...taggedProps },
        children,
      );
    }
  }

  return null;
};

const extractTextContent = (children: React.ReactNode) => {
  if (typeof children === "string") {
    return children.trim();
  }

  if (Array.isArray(children)) {
    return children.map(extractTextContent).pop();
  }

  if (React.isValidElement(children) && children.props.children) {
    return extractTextContent(children.props.children);
  }

  return "";
};

const extractIdsRecursive = (
  node: React.ReactNode,
  extractedIds: any[] = [],
) => {
  if (Array.isArray(node)) {
    React.Children.forEach(node, (child) => {
      if (React.isValidElement(child)) {
        extractedIds = extractIdsRecursive(child, extractedIds);
      }
    });
  } else if (React.isValidElement(node) && node.props.id) {
    const id = node.props.id;
    if (extractTextContent(node.props.children) || node?.props?.title)
      extractedIds.push({
        [id]: { text: extractTextContent(node.props.children), ...node.props },
      });
  } else if (React.isValidElement(node) && node.props.children) {
    React.Children.forEach(node.props.children, (child: any) => {
      if (React.isValidElement(child)) {
        extractedIds = extractIdsRecursive(child, extractedIds);
      }
    });
  }

  return extractedIds;
};

const DOMRenderer = ({
  htmlString,
  className = "",
  showTableOfContent = false,
}) => {
  if (typeof htmlString !== "string") {
    console.error("Non String value passed to DOMRenderer");
    return null;
  }

  const parsedHtml = parse(htmlString || "", {
    replace: (domNode: any) => {
      return parseAndTransformNode(domNode);
    },
  });
  const navAnchors = extractIdsRecursive(parsedHtml);

  return (
    <div className={className}>
      <div className="row">
        {showTableOfContent && <ContentNavigation idMap={navAnchors} />}
        <div className="col mx-auto">{parsedHtml}</div>
      </div>
    </div>
  );
};

export default DOMRenderer;
